<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '@nuxt/ui'
import { brandConfig } from '~/config/brand'

definePageMeta({
  layout: false,
})

const auth = useAuth()
const loading = ref(false)
const showPassword = ref(false)

const schema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type Schema = z.output<typeof schema>

const state = reactive<Schema>({
  email: '',
  password: '',
})

const onSubmit = async (event: FormSubmitEvent<Schema>) => {
  loading.value = true
  try {
    const success = await auth.login({
      email: event.data.email,
      password: event.data.password,
    })

    if (success) {
      await navigateTo('/manage/documents')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen flex">
    <!-- Left side - Branding -->
    <div class="hidden lg:flex lg:w-1/2 bg-primary/5 dark:bg-primary/10 items-center justify-center p-12">
      <div class="max-w-md text-center">
        <div class="flex justify-center mb-8">
          <div class="w-20 h-20 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
            <UIcon name="i-lucide-bot" class="w-10 h-10 text-primary" />
          </div>
        </div>
        <h2 class="text-3xl font-bold text-default mb-4">
          {{ brandConfig.app.name }}
        </h2>
        <p class="text-muted text-lg leading-relaxed">
          {{ brandConfig.app.description }}
        </p>
        <div class="mt-12 flex items-center justify-center gap-8 text-muted">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-file-text" class="w-5 h-5" />
            <span class="text-sm">Documents</span>
          </div>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-message-circle" class="w-5 h-5" />
            <span class="text-sm">Chat</span>
          </div>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-brain" class="w-5 h-5" />
            <span class="text-sm">AI</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right side - Login form -->
    <div class="flex-1 flex items-center justify-center p-6 sm:p-12 bg-default">
      <div class="w-full max-w-sm">
        <!-- Mobile logo -->
        <div class="lg:hidden flex justify-center mb-8">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <UIcon name="i-lucide-bot" class="w-6 h-6 text-primary" />
            </div>
            <span class="text-xl font-bold text-default">{{ brandConfig.app.name }}</span>
          </div>
        </div>

        <div class="text-center lg:text-left mb-8">
          <h1 class="text-2xl font-bold text-default">Welcome back</h1>
          <p class="text-muted mt-2">Sign in to continue to the admin panel</p>
        </div>

        <UCard
          :ui="{
            root: 'ring ring-default shadow-sm',
            body: 'p-6 sm:p-8',
          }"
        >
          <UForm :schema="schema" :state="state" class="space-y-5" @submit="onSubmit">
            <UFormField label="Email" name="email" size="lg" class="min-h-22">
              <UInput
                v-model="state.email"
                type="email"
                placeholder="name@company.com"
                autocomplete="email"
                icon="i-lucide-mail"
                size="lg"
                :ui="{ root: 'w-full' }"
              />
            </UFormField>

            <UFormField label="Password" name="password" size="lg" class="min-h-22">
              <UInput
                v-model="state.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                autocomplete="current-password"
                icon="i-lucide-lock"
                size="lg"
                :ui="{ root: 'w-full' }"
              >
                <template #trailing>
                  <UButton
                    :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    color="neutral"
                    variant="link"
                    size="sm"
                    :padded="false"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </UInput>
            </UFormField>

            <div class="pt-2">
              <UButton type="submit" block size="lg" :loading="loading" icon="i-lucide-log-in"> Sign in </UButton>
            </div>
          </UForm>
        </UCard>

        <p class="text-center text-sm text-muted mt-6">Secure access to {{ brandConfig.app.name }}</p>
      </div>
    </div>
  </div>
</template>
